import {
  BarChart3,
  BookOpen,
  CheckCircle2,
  Columns3,
  Footprints,
  Search,
  Inbox,
  LogOut,
  Monitor,
  Moon,
  Send,
  Briefcase,
  FolderKanban,
  CircleHelp,
  Settings,
  Sun,
  Sunrise,
  UserCheck,
  Users,
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

import { Avatar } from '@/components/ui/avatar';
import { Logo } from '@/components/ui/logo';
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu';
import { useLogout } from '@/hooks/useAuth';
import { LabelDot } from '@/components/tasks/LabelChip';
import { TimerBar } from '@/components/tasks/TimerBar';
import { useLabels } from '@/hooks/useLabels';
import { useTaskCounts } from '@/hooks/useTasks';
import { useTheme, type Theme } from '@/hooks/useTheme';
import type { UserMe } from '@/lib/api/types';
import { useT, type MessageKey } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useIsCompact } from '@/hooks/useMediaQuery';

interface NavItem {
  to: string;
  /** A message key — the sidebar is rendered in the reader's language. */
  label: MessageKey;
  Icon: typeof Sun;
  /** Which count from /tasks/counts to show as a badge. */
  badge?: 'today' | 'upcoming' | 'backlog' | 'assigned_to_me' | 'assigned_by_me';
  /** Render the badge in the overdue colour when this count is non-zero. */
  alertOn?: 'overdue';
}

/** Spec §44: the sidebar, in three groups. */
const PRIMARY: NavItem[] = [
  { to: '/today', label: 'nav.today', Icon: Sun, badge: 'today', alertOn: 'overdue' },
  { to: '/upcoming', label: 'nav.upcoming', Icon: Sunrise, badge: 'upcoming' },
  { to: '/backlog', label: 'nav.backlog', Icon: Inbox, badge: 'backlog' },
  { to: '/board', label: 'nav.board', Icon: Columns3 },
];

const DELEGATION: NavItem[] = [
  {
    to: '/assigned-to-me',
    label: 'nav.assignedToMe',
    Icon: UserCheck,
    badge: 'assigned_to_me',
  },
  {
    to: '/assigned-by-me',
    label: 'nav.assignedByMe',
    Icon: Send,
    badge: 'assigned_by_me',
  },
];

const ARCHIVE: NavItem[] = [
  // Beside Completed, because both answer "what became of it" — one for work
  // that was yours, one for work that passed through you.
  { to: '/involved', label: 'nav.involved', Icon: Footprints },
  { to: '/completed', label: 'nav.completed', Icon: CheckCircle2 },
  { to: '/stats', label: 'nav.stats', Icon: BarChart3 },
];

export function Sidebar({
  user,
  onOpenSearch,
  open = false,
}: {
  user: UserMe;
  onOpenSearch: () => void;
  /** Drawer state. Only consulted below `lg`, where the sidebar overlays. */
  open?: boolean;
}) {
  const t = useT();
  const { data: counts } = useTaskCounts();
  const isCompact = useIsCompact();

  return (
    <aside
      className={cn(
        'flex w-56 shrink-0 flex-col bg-sidebar text-sidebar-content',
        // On a phone there is no room for a permanent 224px column beside the
        // content — it left the task list a sliver. Below `lg` the sidebar
        // slides over the content instead, and `hidden` is deliberately not
        // used: the drawer animates, and a hidden element cannot.
        'max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-50 max-lg:w-[17rem]',
        'max-lg:shadow-2xl max-lg:transition-transform max-lg:duration-200',
        'motion-reduce:max-lg:transition-none',
        open ? 'max-lg:translate-x-0' : 'max-lg:-translate-x-full',
      )}
      // A closed drawer is off-screen but still in the document, so without
      // this its links stay in the tab order and in the screen reader's tree.
      // Guarded on the breakpoint: above `lg` the sidebar is permanent and
      // `open` is meaningless, and marking it inert there would disable the
      // whole navigation on desktop.
      inert={isCompact && !open}
    >
      <div className="px-4 py-4">
        {/* On the green ground the mark is white, and the wordmark's "On"
            picks up the light brand tint rather than the accent used on white. */}
        <Logo
          markLight
          markClassName="h-[18px]"
          nameClassName="text-sidebar-content"
          accentClassName="text-sidebar-accent"
        />
      </div>

      <div className="px-2 pb-1">
        <button
          type="button"
          onClick={onOpenSearch}
          className={cn(
            'flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm',
            'text-sidebar-muted transition-surface hover:bg-sidebar-hover',
            'hover:text-sidebar-content',
          )}
        >
          <Search className="h-4 w-4 shrink-0" aria-hidden />
          <span className="flex-1 truncate text-left">{t('common.search')}</span>
          <kbd className="rounded border border-sidebar-border px-1 text-[10px] text-sidebar-subtle">
            ⌘K
          </kbd>
        </button>
      </div>

      <nav className="scroll-area flex-1 px-2" aria-label={t('nav.label')}>
        <NavGroup items={PRIMARY} counts={counts} />
        <Divider />
        <NavGroup items={DELEGATION} counts={counts} />
        <Divider />
        <NavGroup items={ARCHIVE} counts={counts} />
        <LabelNav />
        <Divider />
        <ul className="space-y-0.5">
          <li>
            <NavLink
              to="/kb"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-surface',
                  isActive
                    ? 'bg-sidebar-active font-medium text-sidebar-content'
                    : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-content',
                )
              }
            >
              <BookOpen className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1 truncate">{t('kb.title')}</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/projects"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-surface',
                  isActive
                    ? 'bg-sidebar-active font-medium text-sidebar-content'
                    : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-content',
                )
              }
            >
              <FolderKanban className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1 truncate">{t('projects.title')}</span>
            </NavLink>
          </li>
          <li>
            <NavLink
              to="/clients"
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-surface',
                  isActive
                    ? 'bg-sidebar-active font-medium text-sidebar-content'
                    : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-content',
                )
              }
            >
              <Briefcase className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1 truncate">{t('clients.title')}</span>
            </NavLink>
          </li>
        </ul>
      </nav>

      <TimerBar />
      <UserMenu user={user} />
    </aside>
  );
}

/**
 * Labels in the sidebar.
 *
 * Only labels that are actually on something are shown, with their count. A
 * list of empty labels is noise, and the point of the sidebar is to be a set of
 * places worth going.
 */
function LabelNav() {
  const t = useT();
  const { data: labels } = useLabels();
  const inUse = (labels ?? []).filter((label) => label.task_count > 0);

  if (inUse.length === 0) return null;

  return (
    <>
      <Divider />
      <p className="px-2.5 pb-1 text-[10px] font-semibold uppercase tracking-wider text-sidebar-subtle">
        {t('nav.labels')}
      </p>
      <ul className="space-y-0.5">
        {inUse.map((label) => (
          <li key={label.id}>
            <NavLink
              to={`/label/${label.id}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-surface',
                  isActive
                    ? 'bg-sidebar-active font-medium text-sidebar-content'
                    : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-content',
                )
              }
            >
              <LabelDot color={label.color} className="ml-1" />
              <span className="flex-1 truncate">{label.name}</span>
              <span className="shrink-0 text-xs tabular-nums text-sidebar-subtle">
                {label.task_count}
              </span>
            </NavLink>
          </li>
        ))}
      </ul>
    </>
  );
}

function Divider() {
  return <div className="my-2 border-t border-sidebar-border" />;
}

function NavGroup({
  items,
  counts,
}: {
  items: NavItem[];
  counts: ReturnType<typeof useTaskCounts>['data'];
}) {
  const t = useT();
  return (
    <ul className="space-y-0.5">
      {items.map(({ to, label, Icon, badge, alertOn }) => {
        const value = badge ? (counts?.[badge] ?? 0) : 0;
        // Today's badge turns red when something has slipped — the sidebar is
        // the one place a user always sees, so that is where the alarm belongs.
        const alerting = alertOn === 'overdue' && (counts?.overdue ?? 0) > 0;
        const shown = alerting ? value + (counts?.overdue ?? 0) : value;

        return (
          <li key={to}>
            <NavLink
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-surface',
                  isActive
                    ? 'bg-sidebar-active font-medium text-sidebar-content'
                    : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-sidebar-content',
                )
              }
            >
              <Icon className="h-4 w-4 shrink-0" aria-hidden />
              <span className="flex-1 truncate">{t(label)}</span>
              {badge && shown > 0 ? (
                <span
                  className={cn(
                    'shrink-0 rounded px-1.5 text-xs tabular-nums',
                    alerting ? 'bg-overdue text-white' : 'text-content-subtle',
                  )}
                  title={
                    alerting
                      ? t('nav.badgeBreakdown', {
                          overdue: counts?.overdue ?? 0,
                          today: value,
                        })
                      : undefined
                  }
                >
                  {shown}
                </span>
              ) : null}
            </NavLink>
          </li>
        );
      })}
    </ul>
  );
}

function UserMenu({ user }: { user: UserMe }) {
  const t = useT();
  const logout = useLogout();
  const { theme, setTheme } = useTheme();

  const themes: { value: Theme; label: MessageKey; Icon: typeof Sun }[] = [
    { value: 'light', label: 'settings.theme.light', Icon: Sun },
    { value: 'dark', label: 'settings.theme.dark', Icon: Moon },
    { value: 'system', label: 'settings.theme.system', Icon: Monitor },
  ];

  return (
    <div className="border-t border-sidebar-border p-2">
      <Menu>
        <MenuTrigger
          className={cn(
            'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left',
            'transition-surface hover:bg-sidebar-hover',
          )}
        >
          <Avatar user={user} size="lg" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-sm text-sidebar-content">
              {user.display_name}
            </span>
            <span className="block truncate text-xs text-sidebar-subtle">
              @{user.username}
            </span>
          </span>
        </MenuTrigger>

        <MenuContent align="start" side="top" className="w-52">
          <MenuItem asChild>
            <NavLink to="/settings">
              <Settings className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
              {t('nav.settings')}
            </NavLink>
          </MenuItem>
          <MenuItem asChild>
            <NavLink to="/teams">
              <Users className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
              {t('nav.teams')}
            </NavLink>
          </MenuItem>
          <MenuItem asChild>
            <NavLink to="/guide">
              <CircleHelp className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
              {t('nav.guide')}
            </NavLink>
          </MenuItem>

          <MenuSeparator />
          {themes.map(({ value, label, Icon }) => (
            <MenuItem
              key={value}
              onSelect={() => setTheme(value)}
              className={theme === value ? 'bg-surface-hover' : ''}
            >
              <Icon className="h-3.5 w-3.5 text-content-subtle" aria-hidden />
              {t(label)}
            </MenuItem>
          ))}

          <MenuSeparator />
          <MenuItem
            onSelect={() => logout.mutate()}
            className="text-danger data-highlighted:bg-danger-subtle"
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden />
            {t('nav.signOut')}
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );
}
